import { useEffect } from "react";
import { useBlocker } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog";

/**
 * Route-level guard for forms with unsaved edits.
 *
 * - In-app navigation: react-router's `useBlocker` intercepts route changes
 *   when `isDirty` is true and pops a confirm dialog. Tab switches and other
 *   in-page state changes are not route changes, so they are not blocked.
 * - Tab close / hard reload / external nav: a `beforeunload` listener trips
 *   the browser's native unsaved-changes prompt.
 *
 * Pass `isSubmitting` (RHF's `formState.isSubmitting`) so the guard is
 * suppressed during the submit handler — which is when consumers call
 * `navigate()` after a successful save. RHF keeps `isSubmitting === true`
 * until the async submit handler returns, so any `navigate()` call inside
 * that handler is unblocked.
 *
 * Returns a `dialog` element the consumer renders anywhere in its tree.
 */
export function useUnsavedChangesGuard(
  isDirty: boolean,
  isSubmitting: boolean = false,
) {
  const active = isDirty && !isSubmitting;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      active && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!active) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);

  const dialog = (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      onClose={() => blocker.reset?.()}
      onConfirm={() => blocker.proceed?.()}
      title="Discard unsaved changes?"
      message="You have unsaved changes on this page. If you leave now, they'll be lost."
      confirmLabel="Discard changes"
      confirmColor="error"
    />
  );

  return { dialog };
}
